import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Container from "@mui/material/Container";
import {
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import Card from "../admincomponents/Card";
import TableComponent from "../admincomponents/Table";
import { useAuth } from "../../../utils/AuthContext";

const GOLD = "#D4AF37";

const CourseApproval = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const theme = useTheme();
  const { token } = useAuth();

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://tdtlworld.com/dss-kaas-backend/lms/courses/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch courses. Please try again.",
        confirmButtonColor: GOLD,
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchCourses();
  }, [fetchCourses, token]);

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Approve Course?",
      text: "This course will be visible to all students.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: GOLD,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    });

    if (result.isConfirmed) {
      try {
        // Assuming API exists. If not, this is a placeholder for the logic.
        // await axios.patch(`https://tdtlworld.com/dss-kaas-backend/lms/admin/course-approval/${id}/`, 
        //   { status: "Approved" },
        //   { headers: { Authorization: `Bearer ${token}` } }
        // );
        
        Swal.fire("Approved!", "The course has been approved.", "success");
        // fetchCourses();
      } catch (error) {
        Swal.fire("Error", "Failed to approve course.", "error");
      }
    }
  };

  const handleReject = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Reject Course",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Enter the reason here...",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      inputValidator: (value) => {
        if (!value) return "You need to provide a reason!";
      },
    });

    if (reason) {
      Swal.fire("Rejected!", "The course has been rejected.", "success");
      // Implementation here
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`https://tdtlworld.com/dss-kaas-backend/lms/courses-delete/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire("Deleted!", "The course has been deleted.", "success");
        fetchCourses();
      } catch (error) {
        Swal.fire("Error", "Failed to delete course.", "error");
      }
    }
  };

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      key: "srNo",
      label: "Sr.No",
      sortable: false,
      render: (row, index) => (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {index + 1}
        </Typography>
      ),
    },
    {
      key: "title",
      label: "Course",
      sortable: true,
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            variant="rounded"
            src={row.cover_img}
            sx={{ width: 45, height: 45, bgcolor: alpha(GOLD, 0.1), border: `1px solid ${alpha(GOLD, 0.2)}` }}
          >
            C
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={700}>{row.title}</Typography>
            <Typography variant="caption" color="text.secondary">ID: {row.course_id}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: "instructor_name",
      label: "Instructor",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>{row.instructor_name || "N/A"}</Typography>
      ),
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      render: (row) => (
        <Chip 
          label={row.category || "General"} 
          size="small" 
          sx={{ bgcolor: alpha(GOLD, 0.05), color: GOLD, fontWeight: 700, borderRadius: "6px" }} 
        />
      ),
    },
    {
      key: "price",
      label: "Price",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" fontWeight={700}>₹{row.price || 0}</Typography>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <Chip
          label={row.status || "Pending"}
          size="small"
          sx={{
            fontWeight: 800,
            fontSize: "0.7rem",
            bgcolor: row.status === "Approved" ? alpha("#4caf50", 0.1) : row.status === "Rejected" ? alpha("#f44336", 0.1) : alpha("#ff9800", 0.1),
            color: row.status === "Approved" ? "#4caf50" : row.status === "Rejected" ? "#f44336" : "#ff9800",
          }}
        />
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => { setSelectedCourse(row); setShowViewModal(true); }} sx={{ color: theme.palette.info.main, bgcolor: alpha(theme.palette.info.main, 0.05), '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) } }}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Approve">
            <IconButton size="small" onClick={() => handleApprove(row.course_id)} sx={{ color: "#4caf50", bgcolor: alpha("#4caf50", 0.05), '&:hover': { bgcolor: alpha("#4caf50", 0.1) } }}>
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton size="small" onClick={() => handleReject(row.course_id)} sx={{ color: "#f44336", bgcolor: alpha("#f44336", 0.05), '&:hover': { bgcolor: alpha("#f44336", 0.1) } }}>
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(row.course_id)} sx={{ color: theme.palette.text.disabled, bgcolor: alpha(theme.palette.text.disabled, 0.05), '&:hover': { bgcolor: alpha(theme.palette.text.disabled, 0.1) } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, fontFamily: "'Outfit', sans-serif", color: theme.palette.text.primary }}>
            Course <Box component="span" sx={{ color: GOLD }}>Management</Box>
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Review and approve courses submitted by instructors.</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FilterIcon />}
          sx={{ 
            borderRadius: "50px", 
            textTransform: 'none',
            fontWeight: 700,
            background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
            color: "#0D0D0D",
            boxShadow: "0 4px 15px rgba(212, 175, 55, 0.3)",
            "&:hover": { 
              background: "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
              boxShadow: "0 6px 20px rgba(212, 175, 55, 0.5)",
            } 
          }}
        >
          Filters
        </Button>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <TextField
            placeholder="Search courses or instructors..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: GOLD }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 350, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
          {loading && <CircularProgress size={24} sx={{ color: GOLD }} />}
        </Box>
      </Paper>

      {/* Table Section */}
      <Box sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        ) : (
          <TableComponent
            columns={columns}
            data={filteredCourses}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Box>

      {/* View Course Details Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, borderBottom: `1px solid ${theme.palette.divider}` }}>
          Course Details: {selectedCourse?.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedCourse && (
            <Box>
              <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                <Box
                  component="img"
                  src={selectedCourse.cover_img}
                  sx={{ width: 200, height: 120, borderRadius: "12px", objectFit: "cover", bgcolor: alpha(GOLD, 0.1) }}
                />
                <Box>
                  <Typography variant="h6" fontWeight={800} color={GOLD}>{selectedCourse.title}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Instructor: <strong>{selectedCourse.instructor_name}</strong></Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <Chip label={selectedCourse.category} size="small" />
                    <Chip label={`₹${selectedCourse.price}`} size="small" variant="outlined" color="primary" />
                  </Box>
                </Box>
              </Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Description</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedCourse.description || "No description provided for this course."}
              </Typography>
              
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Course Stats</Typography>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Enrolled Students</Typography>
                  <Typography variant="body1" fontWeight={700}>{selectedCourse.students || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Rating</Typography>
                  <Typography variant="body1" fontWeight={700}>{selectedCourse.rating || "N/A"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" fontWeight={700}>{new Date(selectedCourse.start_date).toLocaleDateString()}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setShowViewModal(false)} sx={{ color: "text.secondary", fontWeight: 700 }}>Close</Button>
          <Button
            variant="contained"
            onClick={() => { handleApprove(selectedCourse.course_id); setShowViewModal(false); }}
            sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#388e3c" }, borderRadius: "50px", px: 3 }}
          >
            Approve Now
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CourseApproval;
